import { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SlidersHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAddDrop } from '@/hooks/useAddDrop';
import { useWaiverClaims } from '@/hooks/useWaiverClaims';
import { useLeague } from '@/contexts/LeagueContext';
import { useScoringRules } from '@/contexts/ScoringRulesContext';
import { useTimeFilteredRankings } from '@/hooks/useTimeFilteredRankings';
import { useProbablePitchers } from '@/hooks/useProbablePitchers';
import AddDropDialog, { type WaiverMode } from '@/components/mlb-beta/AddDropDialog';
import PlayerCompareDialog from '@/components/mlb-beta/PlayerCompareDialog';
import { type PointsTimeFilter } from '@/lib/timeFilter';
import { MLB_TEAM_ID_TO_ABBR } from '@/data/mlbTeams';
import { type SeasonStats, PITCHER_POSITIONS } from '@/components/mlb-beta/statsTableDefs';
import { AcquisitionStrip } from './players-mobile/AcquisitionStrip';
import {
  FilterSheet, type OwnershipFilter, type StatView,
} from './players-mobile/FilterSheet';
import { PositionPriorityRow, type PlayerType } from './players-mobile/PositionPriorityRow';
import { ActiveFilterChips } from './players-mobile/ActiveFilterChips';
import { SortMenu, type MobileSortKey, type SortDir } from './players-mobile/SortMenu';
import { PlayerCardMobile } from './players-mobile/PlayerCardMobile';
import { getStatColumns, type ScoringMode } from './playersMobileStats';

interface PlayerRow {
  id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: string[] | null;
  mlb_team_abbreviation: string | null;
  mlb_team_name: string | null;
  mlb_id: number | null;
  status: string;
  headshot_url: string | null;
  adp: number;
  stats: SeasonStats | null;
}

type GameInfo = { opponent: string; isHome: boolean; status: string };

const HITTER_POSITIONS = ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'OF', 'DH'];

const FILTER_STORAGE_KEY = 'mlb-players-filters';
const FILTER_VERSION = 6;

const saveFilters = (filters: Record<string, unknown>) => {
  try {
    sessionStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({ ...filters, _v: FILTER_VERSION }),
    );
  } catch { /* sessionStorage unavailable */ }
};

// SAB-352 — regex literal renders as combining marks but matches the
// ̀-ͯ range (diacritic strip). Functionally identical to desktop's
// MLBBetaPlayers.tsx:79 representation. Cosmetic mirror cleanup deferred to a
// follow-up polish ticket.
const normalizeSearchText = (value: string) =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const coerceSortKey = (raw: unknown): MobileSortKey =>
  raw === 'GP' || raw === 'Name' ? raw : 'SPTS';

interface MLBBetaPlayersMobileProps {
  onNavigateToTrades?: () => void;
  onNavigateToPlayer?: (playerId: string) => void;
  scoringMode?: ScoringMode;
}

const MLBBetaPlayersMobile = ({
  onNavigateToTrades,
  onNavigateToPlayer,
  scoringMode = 'sabotage',
}: MLBBetaPlayersMobileProps) => {
  const { toast } = useToast();

  // Hydrate persisted filters (shared sessionStorage key v6 with desktop)
  const stored = useMemo(() => {
    try {
      const raw = JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY) || '{}');
      if (raw._v !== FILTER_VERSION) return {} as Record<string, unknown>;
      return raw as Record<string, unknown>;
    } catch { return {} as Record<string, unknown>; }
  }, []);

  const [search, setSearch] = useState<string>((stored.search as string) || '');
  const [posFilter, setPosFilter] = useState<string>((stored.posFilter as string) || 'All');
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>(() => {
    // Migrate desktop's legacy mine/others to taken (mirrors MLBBetaPlayers.tsx:98-106)
    const v = stored.ownershipFilter;
    if (v === 'mine' || v === 'others') return 'taken';
    if (v === 'all' || v === 'available' || v === 'taken') return v;
    return 'available';
  });
  const [playerType, setPlayerType] = useState<PlayerType>(
    (stored.playerType as PlayerType) || 'all',
  );
  const [statView, setStatView] = useState<StatView>(
    (stored.statView as StatView) || 'season',
  );
  const [sortKey, setSortKey] = useState<MobileSortKey>(coerceSortKey(stored.sortKey));
  const [sortDir, setSortDir] = useState<SortDir>(
    stored.sortDir === 'asc' ? 'asc' : 'desc',
  );
  const [pointsTimeFilter, setPointsTimeFilter] = useState<PointsTimeFilter>(
    (stored.pointsTimeFilter as PointsTimeFilter) || 'season',
  );

  useEffect(() => {
    saveFilters({ search, posFilter, ownershipFilter, playerType, statView, sortKey, sortDir, pointsTimeFilter });
  }, [search, posFilter, ownershipFilter, playerType, statView, sortKey, sortDir, pointsTimeFilter]);

  // Compare flow
  const [compareMode, setCompareMode] = useState(false);
  const [selectedPlayerIds, setSelectedPlayerIds] = useState<Set<string>>(new Set());
  const [compareOpen, setCompareOpen] = useState(false);

  // Per-card chevron expand state. In-memory only — collapses on remount.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Filter sheet
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Hooks
  const addDrop = useAddDrop();
  const waiverClaims = useWaiverClaims();
  const { activeLeague } = useLeague();
  const { scoringRules } = useScoringRules();
  const { rankings, loading: rankingsLoading } = useTimeFilteredRankings(scoringRules, pointsTimeFilter, 2026);
  const { byPlayerId } = useProbablePitchers();

  const waiverMode = (activeLeague?.waiver_type as WaiverMode) || 'fcfs';

  // Add/drop dialog state
  const [addDropOpen, setAddDropOpen] = useState(false);
  const [addTarget, setAddTarget] = useState<{
    id: string;
    full_name: string;
    position_abbreviation: string;
    position_eligibility?: string[] | null;
    mlb_team_abbreviation: string | null;
    headshot_url: string | null;
    player_status?: string;
  } | null>(null);
  const [dropTarget, setDropTarget] = useState<typeof addDrop.roster[number] | null>(null);

  // Players + load state
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  // Today's game schedule (gameInfoMap + probablePitcherMlbIds) — mirrors desktop
  const [gameInfoMap, setGameInfoMap] = useState<Map<string, GameInfo>>(new Map());
  const [probablePitcherMlbIds, setProbablePitcherMlbIds] = useState<Set<number>>(new Set());
  const [twoStartPitcherIds, setTwoStartPitcherIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const fetchGames = async () => {
      const today = new Date().toISOString().slice(0, 10);
      const { data: games } = await supabase
        .from('game_schedule')
        .select('home_team_id, away_team_id, status, home_probable_pitcher_mlb_id, away_probable_pitcher_mlb_id')
        .eq('game_date', today);
      if (!games || games.length === 0) {
        setGameInfoMap(new Map());
        setProbablePitcherMlbIds(new Set());
        return;
      }
      const info = new Map<string, GameInfo>();
      const ppIds = new Set<number>();
      for (const g of games) {
        const homeAbbr = MLB_TEAM_ID_TO_ABBR[g.home_team_id];
        const awayAbbr = MLB_TEAM_ID_TO_ABBR[g.away_team_id];
        const status = g.status || 'scheduled';
        if (homeAbbr) info.set(homeAbbr, { opponent: awayAbbr || '??', isHome: true, status });
        if (awayAbbr) info.set(awayAbbr, { opponent: homeAbbr || '??', isHome: false, status });
        if (g.home_probable_pitcher_mlb_id) ppIds.add(g.home_probable_pitcher_mlb_id);
        if (g.away_probable_pitcher_mlb_id) ppIds.add(g.away_probable_pitcher_mlb_id);
      }
      setGameInfoMap(info);
      setProbablePitcherMlbIds(ppIds);
    };
    fetchGames();
  }, []);

  // 2-start pitchers for the upcoming Mon–Sun week — mirrors desktop
  useEffect(() => {
    const fetchTwoStarts = async () => {
      const now = new Date();
      const day = now.getDay();
      const daysUntilMon = day <= 1 ? 1 - day : 8 - day;
      const monday = new Date(now);
      monday.setDate(now.getDate() + daysUntilMon);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const monStr = monday.toISOString().slice(0, 10);
      const sunStr = sunday.toISOString().slice(0, 10);

      const { data: weekGames } = await supabase
        .from('game_schedule')
        .select('home_probable_pitcher_mlb_id, away_probable_pitcher_mlb_id')
        .gte('game_date', monStr)
        .lte('game_date', sunStr);

      if (!weekGames || weekGames.length === 0) { setTwoStartPitcherIds(new Set()); return; }

      const startCounts = new Map<number, number>();
      for (const g of weekGames) {
        if (g.home_probable_pitcher_mlb_id) startCounts.set(g.home_probable_pitcher_mlb_id, (startCounts.get(g.home_probable_pitcher_mlb_id) || 0) + 1);
        if (g.away_probable_pitcher_mlb_id) startCounts.set(g.away_probable_pitcher_mlb_id, (startCounts.get(g.away_probable_pitcher_mlb_id) || 0) + 1);
      }
      const twoStartMlbIds = new Set<number>();
      for (const [mlbId, count] of startCounts) {
        if (count >= 2) twoStartMlbIds.add(mlbId);
      }
      const ids = new Set<string>();
      for (const p of players) {
        if (p.mlb_id && twoStartMlbIds.has(p.mlb_id)) ids.add(p.id);
      }
      setTwoStartPitcherIds(ids);
    };
    if (players.length > 0) fetchTwoStarts();
  }, [players]);

  // Player query — mirrors MLBBetaPlayers.tsx:218-281 verbatim:
  // sport='mlb' + status IN [Active + 6 IL variants] + season=2026 join.
  // No active_20XX column (desktop doesn't filter by one).
  useEffect(() => {
    let cancelled = false;
    const STATS_COLS = [
      'player_id', 'season',
      'games_played', 'at_bats', 'hits', 'runs_scored', 'doubles', 'triples',
      'home_runs', 'rbi', 'walks_drawn', 'strikeouts', 'stolen_bases', 'caught_stealing',
      'gidp', 'pitcher_wins', 'pitcher_losses', 'pitcher_strikeouts', 'saves',
      'innings_pitched', 'earned_runs', 'walks_allowed', 'home_runs_allowed', 'hits_allowed',
      'is_pitcher', 'games_started_pitching', 'blown_saves', 'wild_pitches', 'hit_batsmen',
      'hit_by_pitch', 'balks', 'passed_balls', 'errors', 'outfield_assists', 'shutouts',
      'complete_games', 'holds', 'left_on_base', 'sac_flies',
    ].join(',');
    const STATS_SELECT = `player_season_stats!left(${STATS_COLS})`;

    const fetchPlayers = async () => {
      setError(null);
      setLoading(true);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors MLBBetaPlayers.tsx:236; Supabase response shape is dynamic
        let allData: any[] = [];

        // Probable-pitcher bypass — mirrors MLBBetaPlayers.tsx. A pitcher named as today's
        // probable starter is findable/addable even if players.status hasn't caught up: MLB posts
        // the probable start before the 40-man recall, so a just-called-up starter reads a status
        // outside the allowlist (e.g. "Reassigned to Minors") across his start. OR the probable
        // mlb_ids (already in game_schedule) into the allowlist. Purely additive.
        // ±1-day window on game_date (mirrors MLBBetaPlayers.tsx): a night game's MLB game_date is
        // the venue-local date, which can trail the client's UTC date by a day, so UTC-today alone
        // drops the starter after 00:00 UTC — during the evening lineup-setting window. A ±1-day
        // window keeps today's announced starters findable across that boundary.
        const dayOffset = (n: number) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
        const { data: bypassGames } = await supabase
          .from('game_schedule')
          .select('home_probable_pitcher_mlb_id, away_probable_pitcher_mlb_id')
          .gte('game_date', dayOffset(-1))
          .lte('game_date', dayOffset(1));
        const probableStarterMlbIds = new Set<number>();
        for (const g of bypassGames || []) {
          if (g.home_probable_pitcher_mlb_id) probableStarterMlbIds.add(g.home_probable_pitcher_mlb_id);
          if (g.away_probable_pitcher_mlb_id) probableStarterMlbIds.add(g.away_probable_pitcher_mlb_id);
        }
        const STATUS_ALLOWLIST = ['Active', 'Injured 10-Day', 'Injured 15-Day', 'Injured 60-Day', 'Injured 7-Day', 'Injured - Full Season', 'Day-To-Day'];
        const statusInList = STATUS_ALLOWLIST.map((s) => `"${s}"`).join(',');
        const orFilter = probableStarterMlbIds.size > 0
          ? `status.in.(${statusInList}),mlb_id.in.(${[...probableStarterMlbIds].join(',')})`
          : null;

        let from = 0;
        const pageSize = 1000;
        while (true) {
          let query = supabase
            .from('players')
            .select(`
              id, full_name, position_abbreviation, position_eligibility,
              mlb_team_abbreviation, mlb_team_name, mlb_id, status, headshot_url,
              adp,
              ${STATS_SELECT}
            `)
            .eq('sport', 'mlb');
          query = orFilter ? query.or(orFilter) : query.in('status', STATUS_ALLOWLIST);
          const { data: batch, error: batchError } = await query
            .eq('player_season_stats.season', 2026)
            .order('adp', { ascending: true })
            .range(from, from + pageSize - 1);
          if (batchError) throw batchError;
          allData = allData.concat(batch || []);
          if (!batch || batch.length < pageSize) break;
          from += pageSize;
        }

        if (cancelled) return;

        const mapped: PlayerRow[] = allData.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors MLBBetaPlayers.tsx:261; row shape is dynamic
          (p: any) => {
            const statsArr = p.player_season_stats;
            const stats: SeasonStats | null = Array.isArray(statsArr) && statsArr.length > 0
              ? statsArr[0]
              : statsArr && !Array.isArray(statsArr) ? statsArr : null;
            return { ...p, player_season_stats: undefined, stats };
          },
        );

        setPlayers(mapped);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        console.error('[MLBBetaPlayersMobile] fetch failed:', err);
        Sentry.captureException(err, { tags: { component: 'MLBBetaPlayersMobile', op: 'fetchPlayers' } });
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      }
    };
    fetchPlayers();
    return () => { cancelled = true; };
  }, [loadAttempt]);

  // Still-loading toast (parity with desktop)
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => {
      toast({
        title: 'Still loading…',
        description: 'Fetching players over a slow connection. Hang tight.',
      });
    }, 10000);
    return () => clearTimeout(t);
  }, [loading, loadAttempt, toast]);

  // Position matching — mirrors desktop matchesPos
  const matchesPos = (
    pos: string,
    filter: string,
    playerId?: string,
    eligibility?: string[] | Record<string, number> | null,
    mlbId?: number | null,
  ) => {
    if (filter === 'All') return true;
    if (filter === 'TwoStart') return playerId ? twoStartPitcherIds.has(playerId) : false;
    if (filter === 'ProbablePitchers') return mlbId ? probablePitcherMlbIds.has(mlbId) : false;
    const eligArr = Array.isArray(eligibility) ? eligibility : eligibility ? Object.keys(eligibility) : [];
    const allPos = new Set([pos, ...eligArr]);
    if (filter === 'Hitters') return [...allPos].some(p => HITTER_POSITIONS.includes(p) || p === 'TWP');
    if (filter === 'Pitchers') return [...allPos].some(p => PITCHER_POSITIONS.includes(p));
    if (filter === 'OF') return [...allPos].some(p => ['LF', 'CF', 'RF', 'OF'].includes(p));
    return allPos.has(filter);
  };

  // Filtered list — mirrors desktop filter pipeline
  const filtered = useMemo(() => {
    let list = players;
    list = list.filter(p => matchesPos(p.position_abbreviation, posFilter, p.id, p.position_eligibility, p.mlb_id));
    if (playerType === 'batters') {
      list = list.filter(p => !p.stats?.is_pitcher || HITTER_POSITIONS.includes(p.position_abbreviation));
    } else if (playerType === 'pitchers') {
      list = list.filter(p => p.stats?.is_pitcher || PITCHER_POSITIONS.includes(p.position_abbreviation));
    }
    if (search) {
      const q = normalizeSearchText(search);
      list = list.filter(p =>
        normalizeSearchText(p.full_name).includes(q) ||
        normalizeSearchText(p.mlb_team_name || '').includes(q) ||
        normalizeSearchText(p.mlb_team_abbreviation || '').includes(q),
      );
    }
    if (ownershipFilter === 'available') {
      list = list.filter(p => addDrop.isFreeAgent(p.id));
    } else if (ownershipFilter === 'taken') {
      list = list.filter(p => !addDrop.isFreeAgent(p.id));
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, search, posFilter, ownershipFilter, playerType, addDrop, twoStartPitcherIds, probablePitcherMlbIds]);

  // Cross-tab match — mirrors desktop MLB-062
  const crossTabMatch = useMemo(() => {
    if (!search || playerType === 'all') return null;
    const q = normalizeSearchText(search);
    const matchesSearch = (p: PlayerRow) =>
      normalizeSearchText(p.full_name).includes(q) ||
      normalizeSearchText(p.mlb_team_name || '').includes(q) ||
      normalizeSearchText(p.mlb_team_abbreviation || '').includes(q);
    let other = 0;
    for (const p of players) {
      if (!matchesPos(p.position_abbreviation, posFilter, p.id, p.position_eligibility, p.mlb_id)) continue;
      if (!matchesSearch(p)) continue;
      const isPitcher = p.stats?.is_pitcher || PITCHER_POSITIONS.includes(p.position_abbreviation);
      if (playerType === 'batters' && isPitcher) other++;
      else if (playerType === 'pitchers' && !isPitcher) other++;
    }
    return other > 0
      ? { count: other, other: playerType === 'batters' ? 'pitchers' : 'batters' as const }
      : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, playerType, posFilter, players, twoStartPitcherIds, probablePitcherMlbIds]);

  const { tpMap, gpMap } = useMemo(() => {
    const tp = new Map<string, number>();
    const gp = new Map<string, number>();
    for (const r of rankings) {
      tp.set(r.player_id, r.net_points);
      gp.set(r.player_id, r.games_played);
    }
    return { tpMap: tp, gpMap: gp };
  }, [rankings]);

  const getTP = (p: PlayerRow) => tpMap.get(p.id) || 0;

  // Sort — 3 mobile sort keys (SPTS, GP, Name). Uses tpMap directly (not the
  // getTP closure) so the deps array stays accurate without recreating the
  // memo every render.
  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sortKey === 'SPTS') {
      list.sort((a, b) => {
        let av = tpMap.get(a.id) || 0;
        let bv = tpMap.get(b.id) || 0;
        if (statView === 'pergame') {
          const gpA = gpMap.get(a.id) || a.stats?.games_played || 0;
          const gpB = gpMap.get(b.id) || b.stats?.games_played || 0;
          av = gpA > 0 ? av / gpA : 0;
          bv = gpB > 0 ? bv / gpB : 0;
        }
        return sortDir === 'desc' ? bv - av : av - bv;
      });
    } else if (sortKey === 'GP') {
      // SAB-364: in 7d/14d/30d views, sort by the windowed GP (gpMap) so the
      // sort order matches the displayed value. Season view falls back to
      // the season total from player_season_stats.
      const useWindow = pointsTimeFilter !== 'season';
      list.sort((a, b) => {
        const av = useWindow ? (gpMap.get(a.id) ?? 0) : (a.stats?.games_played ?? 0);
        const bv = useWindow ? (gpMap.get(b.id) ?? 0) : (b.stats?.games_played ?? 0);
        return sortDir === 'desc' ? bv - av : av - bv;
      });
    } else {
      list.sort((a, b) => {
        const cmp = a.full_name.localeCompare(b.full_name);
        return sortDir === 'desc' ? -cmp : cmp;
      });
    }
    return list;
  }, [filtered, sortKey, sortDir, statView, tpMap, gpMap, pointsTimeFilter]);

  // Filter sheet badge count (excludes posFilter — that's in Band 3, not the sheet)
  const filterCount =
    (ownershipFilter !== 'available' ? 1 : 0) +
    (pointsTimeFilter !== 'season' ? 1 : 0) +
    (statView !== 'season' ? 1 : 0);

  // Handlers ----------------------------------------------------------------

  const togglePlayerSelection = (id: string) => {
    setSelectedPlayerIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onToggleCompare = () => {
    if (compareMode) {
      setCompareMode(false);
      setSelectedPlayerIds(new Set());
    } else {
      setCompareMode(true);
    }
  };

  const onConfirmCompare = () => {
    if (selectedPlayerIds.size < 2) return;
    setCompareOpen(true);
  };

  const onPlayerTypeChange = (value: PlayerType) => {
    setPlayerType(value);
    setSortKey('SPTS');
    setSortDir('desc');
  };

  const onPointsTimeFilterChange = (value: PointsTimeFilter) => {
    setPointsTimeFilter(value);
    setSortKey('SPTS');
    setSortDir('desc');
  };

  const clearAllChips = () => {
    setPosFilter('All');
    setOwnershipFilter('available');
    setPointsTimeFilter('season');
    setStatView('season');
  };

  const handleAddClick = (player: {
    id: string;
    full_name: string;
    position_abbreviation: string;
    position_eligibility?: string[] | null;
    mlb_team_abbreviation: string | null;
    headshot_url: string | null;
    player_status?: string;
  }) => {
    setAddTarget(player);
    setDropTarget(null);
    setAddDropOpen(true);
  };

  const handleDropClick = (playerId: string) => {
    const rp = addDrop.getMyRosterPlayer(playerId);
    if (!rp) return;
    setDropTarget(rp);
    setAddTarget(null);
    setAddDropOpen(true);
  };

  // Virtualizer — window-scrolled with dynamic row sizing for chevron toggles
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const virtualizer = useWindowVirtualizer({
    count: sorted.length,
    estimateSize: () => 76,
    overscan: 8,
    measureElement: (el) => el?.getBoundingClientRect().height ?? 76,
    scrollMargin: tableContainerRef.current?.offsetTop ?? 0,
  });

  // Render ------------------------------------------------------------------

  const showAcquisitionStrip = !!addDrop.myTeam || compareMode;

  return (
    <div className="flex flex-col bg-background min-h-screen pb-16">
      {/* Title row — eyebrow dropped per Adam's call (no currentWeek source) */}
      <div className="px-4 pt-3 pb-1.5 flex items-baseline justify-between">
        <h1 className="text-2xl font-display font-extrabold tracking-tight text-foreground">Players</h1>
        <span className="text-[11px] font-display font-semibold uppercase tracking-wider text-muted-foreground">
          MLB
        </span>
      </div>

      {/* Band 1 — AcquisitionStrip (not sticky) */}
      {showAcquisitionStrip && (
        <AcquisitionStrip
          acquisitionsUsed={addDrop.acquisitionsUsed}
          maxAcquisitions={addDrop.maxAcquisitions}
          faabRemaining={addDrop.faabRemaining}
          waiverMode={waiverMode}
          compareMode={compareMode}
          selectedCount={selectedPlayerIds.size}
          onToggleCompare={onToggleCompare}
          onConfirmCompare={onConfirmCompare}
        />
      )}

      {/* Band 2 — sticky search + filter icon */}
      <div className="sticky top-[env(safe-area-inset-top)] z-20 bg-background px-4 py-1.5 flex gap-2">
        <Input
          placeholder="Search players or teams…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 h-10 bg-card border-border"
        />
        <Button
          variant="outline"
          size="icon"
          className={cn(
            'h-10 w-10 relative shrink-0',
            filterCount > 0 && 'border-primary text-primary',
          )}
          onClick={() => setFilterSheetOpen(true)}
          aria-label="Filters"
        >
          <SlidersHorizontal className="w-4 h-4" />
          {filterCount > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center px-1"
              aria-label={`${filterCount} active filter${filterCount === 1 ? '' : 's'}`}
            >
              {filterCount}
            </span>
          )}
        </Button>
      </div>

      {/* Band 3 — sticky position priority */}
      <div className="sticky top-[calc(env(safe-area-inset-top)+52px)] z-[19] bg-background px-4 pt-1 pb-2.5">
        <PositionPriorityRow
          playerType={playerType}
          onPlayerTypeChange={onPlayerTypeChange}
          posFilter={posFilter}
          onPosFilterChange={setPosFilter}
        />
      </div>

      {/* Band 4 — active filter chips (conditional, not sticky) */}
      <ActiveFilterChips
        posFilter={posFilter}
        ownershipFilter={ownershipFilter}
        pointsTimeFilter={pointsTimeFilter}
        statView={statView}
        onResetPos={() => setPosFilter('All')}
        onResetOwnership={() => setOwnershipFilter('available')}
        onResetTime={() => setPointsTimeFilter('season')}
        onResetStatView={() => setStatView('season')}
        onClearAll={clearAllChips}
      />

      {/* Cross-tab match banner — mirrors desktop MLB-062, copy verbatim */}
      {crossTabMatch && (
        <div className="mx-4 mt-1 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 flex items-center justify-between gap-2 text-xs">
          <span className="text-foreground">
            <span className="font-semibold">{crossTabMatch.count}</span> more {crossTabMatch.other}{' '}
            {crossTabMatch.count === 1 ? 'matches' : 'match'}{' '}
            <span className="font-mono text-primary">"{search}"</span>.
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-primary shrink-0"
            onClick={() => setPlayerType('all')}
          >
            Switch to All →
          </Button>
        </div>
      )}

      {/* Result count + sort */}
      <div className="px-4 py-2 flex items-center justify-between text-[11px] font-display font-semibold uppercase tracking-wider text-muted-foreground">
        <span>
          {sorted.length.toLocaleString()} player{sorted.length === 1 ? '' : 's'}
          {rankingsLoading && pointsTimeFilter !== 'season' && (
            <span className="ml-1.5 normal-case font-normal animate-pulse">· loading…</span>
          )}
        </span>
        <SortMenu
          sortKey={sortKey}
          sortDir={sortDir}
          scoringMode={scoringMode}
          onSortKeyChange={setSortKey}
          onSortDirChange={setSortDir}
        />
      </div>

      {/* List rendering — Diff 2 will replace this stub with the virtualized list */}
      {loading ? (
        <div className="px-4 space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[68px] rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <div className="mx-4 my-3 rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center space-y-3">
          <p className="text-sm font-semibold text-destructive">Failed to load players.</p>
          <p className="text-xs text-muted-foreground">
            {error.message || 'A network or server error occurred.'}
          </p>
          <Button size="sm" onClick={() => setLoadAttempt(n => n + 1)}>Tap to retry</Button>
        </div>
      ) : sorted.length === 0 ? (
        <div className="mx-4 my-6 text-sm text-muted-foreground text-center">
          {posFilter === 'TwoStart'
            ? 'No two-start pitchers confirmed yet. MLB announces probable pitchers ~3 days ahead'
            : 'No players found'}
        </div>
      ) : (
        <div
          ref={tableContainerRef}
          className="relative"
          style={{ height: virtualizer.getTotalSize() }}
        >
          {virtualizer.getVirtualItems().map((vi) => {
            const player = sorted[vi.index];
            if (!player) return null;
            const positionType: 'batter' | 'pitcher' =
              player.stats?.is_pitcher || PITCHER_POSITIONS.includes(player.position_abbreviation)
                ? 'pitcher'
                : 'batter';
            const columns = getStatColumns(scoringMode, positionType, pointsTimeFilter);
            const gamesPlayed = pointsTimeFilter === 'season'
              ? (player.stats?.games_played ?? 0)
              : (gpMap.get(player.id) ?? 0);
            const gameInfo = player.mlb_team_abbreviation
              ? gameInfoMap.get(player.mlb_team_abbreviation)
              : undefined;
            const isProbPitcher = !!player.mlb_id && probablePitcherMlbIds.has(player.mlb_id);

            return (
              <div
                key={player.id}
                data-index={vi.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vi.start - virtualizer.options.scrollMargin}px)`,
                }}
              >
                <div className="px-4 pb-2">
                  <PlayerCardMobile
                    player={{
                      id: player.id,
                      full_name: player.full_name,
                      position_abbreviation: player.position_abbreviation,
                      position_eligibility: player.position_eligibility,
                      mlb_team_abbreviation: player.mlb_team_abbreviation,
                      mlb_id: player.mlb_id,
                      status: player.status,
                      headshot_url: player.headshot_url,
                      stats: player.stats,
                    }}
                    rank={vi.index + 1}
                    spts={getTP(player)}
                    scoringMode={scoringMode}
                    columns={columns}
                    statView={statView}
                    gamesPlayed={gamesPlayed}
                    gameInfo={gameInfo}
                    pitcherStartInfo={byPlayerId.get(player.id) || null}
                    isProbablePitcher={isProbPitcher}
                    compareMode={compareMode}
                    isSelected={selectedPlayerIds.has(player.id)}
                    isExpanded={expandedIds.has(player.id)}
                    addDrop={addDrop}
                    waiverMode={waiverMode}
                    onCardClick={() => onNavigateToPlayer?.(player.id)}
                    onToggleSelected={() => togglePlayerSelection(player.id)}
                    onToggleExpanded={() => toggleExpanded(player.id)}
                    onAddClick={(p) => handleAddClick({
                      id: p.id,
                      full_name: p.full_name,
                      position_abbreviation: p.position_abbreviation,
                      position_eligibility: p.position_eligibility,
                      mlb_team_abbreviation: p.mlb_team_abbreviation,
                      headshot_url: p.headshot_url,
                      player_status: p.status,
                    })}
                    onDropClick={handleDropClick}
                    onTradeClick={() => onNavigateToTrades?.()}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <FilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        ownershipFilter={ownershipFilter}
        onOwnershipChange={setOwnershipFilter}
        pointsTimeFilter={pointsTimeFilter}
        onPointsTimeFilterChange={onPointsTimeFilterChange}
        statView={statView}
        onStatViewChange={setStatView}
      />

      <AddDropDialog
        open={addDropOpen}
        onOpenChange={setAddDropOpen}
        addPlayer={addTarget}
        dropPlayer={dropTarget}
        roster={addDrop.roster}
        faabRemaining={addDrop.faabRemaining}
        processing={addDrop.processing}
        onConfirm={addDrop.executeAddDrop}
        onAdd={addDrop.executeAdd}
        onAddToIL={addDrop.executeAddToIL}
        onSubmitClaim={(playerId, faabBid, dropPlayerId) =>
          waiverClaims.submitClaim(playerId, faabBid, dropPlayerId, undefined, addTarget ? addDrop.getWaiverWireId(addTarget.id) || undefined : undefined)
        }
        waiverMode={waiverMode}
        isOnWaiverWire={addTarget ? addDrop.isOnWaiverWire(addTarget.id) : false}
        waiverExpiresAt={addTarget ? addDrop.getWaiverExpiry(addTarget.id) : null}
        checkPositionLimit={addDrop.checkPositionLimit}
        checkSlotEligibility={addDrop.checkSlotEligibility}
        isAddPlayerILEligible={addTarget ? addDrop.isFreeAgentILEligible(addTarget) : false}
        availableILSlots={addDrop.availableILSlots}
        openActiveSlots={addDrop.openActiveSlots}
        totalRosterSize={addDrop.totalRosterSize}
      />

      <PlayerCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        initialPlayerIdA={Array.from(selectedPlayerIds)[0] || null}
        initialPlayerIdB={Array.from(selectedPlayerIds)[1] || null}
      />
    </div>
  );
};

export default MLBBetaPlayersMobile;
